import { CloseOutlined, InfoCircleOutlined } from '@ant-design/icons';
import {
  AddonSettingsKeyType,
  AddonSettingsValueType,
  EnhancedPlan,
} from '@mm/state/interfaces/subscription';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { getCurrentSubscription } from '@mm/state/selectors/subscriptionSelectors';
import { centsToDollars } from '@mm/utils';
import classNames from 'classnames';
import { uniqueId } from 'lodash';
import React, { MouseEvent } from 'react';
import { useSelector } from 'react-redux';
import styles from './BillingPriceTableModal.module.css';

type Props = {
  type: AddonSettingsKeyType;
  pageId: number;
  plan?: EnhancedPlan;
  onClose?: () => void;
};

export const BillingPriceTableModal = ({
  type,
  pageId,
  plan,
  onClose,
}: Props) => {
  const { closeModal } = useModalActions();
  const INFINITY_VALUE = 999999999;
  const currentSubscription = useSelector(getCurrentSubscription(pageId));
  const isManagedContacts = type === 'contactsManagement';

  const modalTitle = () => {
    switch (type) {
      case 'xrayLeads':
        return 'X-Ray Leads';
      case 'emailSends':
        return 'Email Sends';
      case 'mercuryLeads':
        return 'Consumer Directory Leads';
      case 'socialSends':
        return 'Social Sends';
      case 'smsSends':
        return 'SMS/MMS';
      case 'contactsManagement':
        return 'Monthly Contact Management Fee';
      default:
        return '';
    }
  };

  const close = (e: MouseEvent<HTMLSpanElement>) => {
    e.stopPropagation();
    if (onClose) {
      onClose?.();
      return;
    }
    closeModal();
  };

  const addonSettingsByType = Object.keys(
    currentSubscription?.addonSettings || {},
  ).length
    ? currentSubscription?.addonSettings?.[type]
    : plan?.addonSettings?.[type];

  const dataByType: AddonSettingsValueType[] = addonSettingsByType
    ? JSON.parse(addonSettingsByType)
    : [];

  const isMultipleEntries = dataByType.length > 1;
  const singularTitle =
    type !== 'smsSends'
      ? isManagedContacts
        ? '1,000 contacts'
        : modalTitle().slice(0, -1)
      : 'SMS';

  return (
    <div className={styles.container}>
      <CloseOutlined className={styles.closeIcon} onClick={e => close(e)} />
      <div className={styles.title}>
        {modalTitle()} {isMultipleEntries && <br />}
        {!isManagedContacts && (
          <span>Pricing {isMultipleEntries ? 'Breakdown' : ''}</span>
        )}
      </div>
      {type === 'smsSends' && (
        <div className={styles.alert}>
          <InfoCircleOutlined className={styles.infoIcon} />1 MMS costs as 2 SMS
        </div>
      )}
      <div className={styles.tableContainer}>
        {isMultipleEntries && (
          <div className={styles.tableHeader}>
            <div className={classNames(styles.text, styles.bold500)}>
              {isManagedContacts ? 'Totals contacts' : 'Packages'}
            </div>
            <div className={classNames(styles.text, styles.bold500)}>
              Cost per additional {singularTitle}
            </div>
          </div>
        )}
        {dataByType?.map((data, index) => {
          const qty = data?.ending_quantity;
          const prevQty = dataByType?.[index - 1]?.ending_quantity;
          const previousEndingQuantity =
            prevQty && (isManagedContacts ? prevQty * 1000 + 1 : prevQty + 1);

          const isInfinity = qty === INFINITY_VALUE;
          const packageValue = (
            isInfinity && previousEndingQuantity
              ? previousEndingQuantity
              : isManagedContacts
              ? (qty || 0) * 1000
              : qty
          )?.toLocaleString();

          const price = centsToDollars(
            Number(data?.unit_amount_in_cents?.USD) || 0,
          );
          return isMultipleEntries ? (
            <div className={styles.tableRow} key={uniqueId()}>
              <div className={styles.text}>
                {index === 0 && 'Up to '}
                {previousEndingQuantity &&
                  !isInfinity &&
                  `${previousEndingQuantity?.toLocaleString()} - `}{' '}
                {packageValue}
                {`${isInfinity ? '+' : ''}`}
              </div>
              <div className={classNames(styles.text, styles.bold500)}>
                {isManagedContacts && '+'}${price}
              </div>
            </div>
          ) : (
            <div className={styles.text} key={uniqueId()}>
              ${price} per {singularTitle}
            </div>
          );
        })}
      </div>
      {type === 'contactsManagement' && (
        <div className={classNames(styles.contactManagementDescription)}>
          <span className={styles.bold700}>Contact management includes:</span>
          <ul className={styles.unorderedList}>
            <li>1 email send per month</li>
            <li>Enrichment of leads - updated monthly</li>
            <li>In market analysis of leads</li>
            <li>Returning visitor monitor</li>
            <li>Server side tracking</li>
            <li>Customer.ai lead scoring intent</li>
          </ul>
        </div>
      )}
    </div>
  );
};
